import { Alerts } from 'constants/alerts';

import { useMemo, useState } from 'react';

import { useApiContext } from 'context/apiContext';
import { useRecordModal } from 'hooks/useRecordModal';
import { FILTER_RESET_VALUES } from 'pages/Monitoring/OrderProviderExternalErrors/constants/filterResetValues';
import { useGetOrderProviderExternalErrorsChartData } from 'pages/Monitoring/OrderProviderExternalErrors/hooks/useGetOrderProviderExternalErrorsChartData';
import { OrderProviderExternalErrorsDatagrid } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsDatagrid/OrderProviderExternalErrorsDatagrid';
import {
  OrderProviderExternalErrorsFilter,
  OrderProviderExternalErrorsFilterValues,
} from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsFilter/OrderProviderExternalErrorsFilter';
import { OrderProviderExternalErrorsFilterChips } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsFilter/OrderProviderExternalErrorsFilterChips';
import { OrderProviderExternalErrorsPeriodChart } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsPeriodChart/OrderProviderExternalErrorsPeriodChart';
import { OrderProviderExternalErrorsRatioChart } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsRatioChart/OrderProviderExternalErrorsRatioChart';
import { OrderProviderExternalErrorsSummaryDialog } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsSummary/OrderProviderExternalErrorsSummaryDialog/OrderProviderExternalErrorsSummaryDialog';
import { OrderProviderExternalErrorsSummaryGatewaysDialog } from 'pages/Monitoring/OrderProviderExternalErrors/OrderProviderExternalErrorsSummary/OrderProviderExternalErrorsSummaryGatewaysDialog/OrderProviderExternalErrorsSummaryGatewaysDialog';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext, WithListContext } from 'react-admin';
import { DefaultValues, FormProvider } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { EmptyListAlert } from 'shared/mui/Alert/EmptyListAlert/EmptyListAlert';
import { SuccessReportAlert } from 'shared/mui/Alert/SuccessReportAlert/SuccessReportAlert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { Stack } from 'shared/mui/Stack/Stack';
import { Tab } from 'shared/mui/Tab/Tab';
import { TabPanel } from 'shared/mui/TabPanel/TabPanel';
import { Tabs } from 'shared/mui/Tabs/Tabs';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { Gateway } from 'types/embedded/gateway';
import { convertReactAdminIdToHydraId, getId } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

export const OrderProviderExternalErrorsListContent: React.FC = () => {
  const translate = useTypedTranslate();
  const { user } = useApiContext();
  const [showSuccessAlert, setShowSuccessAlert] = useState(true);
  const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
  const toggleShowSummaryDialog = () => setOpenSummaryDialog((open) => !open);
  const {
    handleCloseRecordModal,
    openRecordModal,
    handleOpenRecordModal,
    record: gatewayRecord,
  } = useRecordModal<Gateway>();

  const [currentTab, setCurrentTab] = useState(0);
  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const { filterValues, data } = useListContext();

  const { data: chartData, isLoading: isChartLoading } =
    useGetOrderProviderExternalErrorsChartData(filterValues);

  const isFiltersApplied = useMemo(() => Object.keys(filterValues).length > 0, [filterValues]);

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<OrderProviderExternalErrorsFilterValues>({
      mode: 'all',
      defaultValues: {
        appliedDate: [
          formatInputToTimestamp(filterValues?.createdAt?.gte),
          formatInputToTimestamp(filterValues?.createdAt?.lte),
        ],
        timezone: filterValues.timezone || null,
        providerExternalError: {
          isCritical: filterValues?.providerExternalError?.isCritical || null,
          category: filterValues?.providerExternalError?.category
            ? filterValues?.providerExternalError?.category.map((category: string) =>
                convertReactAdminIdToHydraId(category, 'external_error_categories'),
              )
            : null,
          reason: filterValues?.providerExternalError?.reason || null,
        },
        merchant: filterValues?.merchant || [],
        provider: filterValues?.provider || [],
        method: filterValues?.method || [],
        order: {
          direction: filterValues?.order?.direction || null,
          gateway: filterValues?.order?.gateway || null,
        },
      },
      resetValues: FILTER_RESET_VALUES,
    });

  useSyncFilter<OrderProviderExternalErrorsFilterValues>({
    appliedFilters,
    transform: (
      filterValues: DefaultValues<OrderProviderExternalErrorsFilterValues>,
    ): OrderProviderExternalErrorsFilterValues => {
      const {
        appliedDate = [null, null],
        timezone,
        providerExternalError,
        ...restFilters
      } = filterValues;
      return cleanEmpty({
        ...restFilters,
        providerExternalError: {
          ...providerExternalError,
          category: providerExternalError?.category
            ? providerExternalError?.category.map((category) => getId(category))
            : null,
        },
        createdAt: {
          gte: formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
          lte: formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        },
        timezone: getAppliedTimezone(timezone, appliedDate),
        order: {
          direction: restFilters?.order?.direction || null,
          gateway: restFilters?.order?.gateway,
        },
      });
    },
  });

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton disabled={!data?.length} key="select-column" />,
          <FilterClearButton
            key="clear"
            onClick={() => {
              onReset();
            }}
            visible={isFiltersApplied}
          />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <OrderProviderExternalErrorsFilter />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label={translate('app.buttons.reset')} onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider
          appliedFilters={appliedFilters}
          onReset={onReset}
          setAppliedFilter={setAppliedFilter}
        >
          <FormProvider {...form}>
            <OrderProviderExternalErrorsFilterChips />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
      <WithListContext
        render={({ isLoading, data }) => {
          if (user.alerts.includes(Alerts.EmptyMerchantAccess)) {
            return (
              <Alert severity="warning">
                {translate('app.filter.noMerchantsAvailableForUser', {
                  firstName: user.firstName,
                  lastName: user.lastName,
                })}
              </Alert>
            );
          }
          return (
            <Box sx={{ marginBottom: '24px' }}>
              {(isChartLoading || chartData) && (
                <>
                  <Tabs onChange={onTabChange} value={currentTab}>
                    <Tab label={translate('app.orderProviderExternalErrors.declineEvents')} />
                  </Tabs>
                  <TabPanel index={0} value={currentTab}>
                    <Stack
                      direction="row"
                      gap="16px"
                      sx={{ marginBottom: '16px', paddingTop: '16px' }}
                    >
                      <OrderProviderExternalErrorsRatioChart data={chartData?.ratio_by_providers} />
                      <OrderProviderExternalErrorsPeriodChart data={chartData?.reasons_by_period} />
                    </Stack>
                  </TabPanel>
                </>
              )}
              {!!data?.length && showSuccessAlert && (
                <SuccessReportAlert
                  actionButton={
                    <Button
                      color="inherit"
                      label={translate('app.orderProviderExternalErrors.summaryForThePeriod')}
                      onClick={toggleShowSummaryDialog}
                      size="small"
                      variant="text"
                    />
                  }
                  date={filterValues.createdAt}
                  onClose={() => setShowSuccessAlert(false)}
                  timezone={filterValues.timezone}
                />
              )}
              {!isFiltersApplied && <EmptyListAlert onClick={toggleDrawer(true)} />}
              {isFiltersApplied &&
                (isLoading ? (
                  <DatagridSkeleton columnsCount={12} />
                ) : (
                  <OrderProviderExternalErrorsDatagrid
                    handleOpenGatewaySummaryDialog={handleOpenRecordModal}
                    timezone={filterValues?.timezone}
                  />
                ))}
            </Box>
          );
        }}
      />
      {openSummaryDialog && (
        <OrderProviderExternalErrorsSummaryDialog
          filterValues={filterValues}
          handleOpenGatewaySummaryDialog={handleOpenRecordModal}
          onClose={toggleShowSummaryDialog}
          open={openSummaryDialog}
        />
      )}
      {gatewayRecord && (
        <OrderProviderExternalErrorsSummaryGatewaysDialog
          filterValues={filterValues}
          gatewayId={gatewayRecord.id}
          gatewayName={gatewayRecord.name}
          onClose={handleCloseRecordModal}
          open={openRecordModal}
        />
      )}
    </>
  );
};
